import { Node, Edge } from 'reactflow';
import * as dagre from '@dagrejs/dagre';
import { findRootNodes } from '../utils/GraphUtils';

/**
 * Apply dagre layout to nodes and edges
 */
export const getLayoutedElements = (
  nodes: Node[], 
  edges: Edge[], 
  direction: 'TB' | 'BT' | 'LR' = 'TB',
  isRootNodeView: boolean = false
): { nodes: Node[], edges: Edge[] } => {
  if (nodes.length === 0) return { nodes, edges };

  // Find root nodes (nodes with no incoming edges)
  const rootNodes = findRootNodes(nodes, edges);
  const isSimpleStructure = rootNodes.length === nodes.length; // All nodes are root nodes
  
  // For simple structures with all root nodes, arrange horizontally
  if (isSimpleStructure) {
    // Increase spacing for better readability
    const horizontalSpacing = 300; // Increased from 220
    const startX = 100; // Increased from 50
    const baseY = 100; // Base Y position for all nodes
    
    // Sort nodes by name for consistent ordering
    const sortedNodes = [...nodes].sort((a, b) => {
      const nameA = a.data.label.toLowerCase();
      const nameB = b.data.label.toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    // Position nodes horizontally with adaptive spacing based on node count
    const layoutedNodes = sortedNodes.map((node, index) => ({
      ...node,
      position: {
        x: startX + (index * horizontalSpacing),
        y: baseY
      }
    }));
    
    return { nodes: layoutedNodes, edges };
  }

  // For each root node, identify its complete dependency tree
  const dependencyGroups = new Map<string, Set<string>>();
  rootNodes.forEach(rootNode => {
    const dependencies = new Set<string>();
    
    // Recursive function to find all dependencies
    const findDependencies = (nodeId: string) => {
      edges.forEach(edge => {
        if (edge.source === nodeId && !dependencies.has(edge.target)) {
          dependencies.add(edge.target);
          findDependencies(edge.target);
        }
      });
    };
    
    findDependencies(rootNode.id);
    dependencyGroups.set(rootNode.id, dependencies);
  });

  // For more complex structures, use dagre but ensure root nodes are aligned
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  
  // Configure dagre with appropriate spacing based on view type
  if (isRootNodeView && nodes.length > 10) {
    console.log("Using specialized layout for root node view with many nodes");
    
    // Use a more compact layout for root node view with many nodes
    dagreGraph.setGraph({ 
      rankdir: direction,
      nodesep: 80,   // Reduced horizontal spacing for root view
      ranksep: 120,  // Reduced vertical spacing for root view
      edgesep: 30,   // Reduced edge spacing for root view
      marginx: 30,   // Reduced margin for root view
      marginy: 30
    });
  } else {
    // Standard spacing for focus node view or small graphs
    dagreGraph.setGraph({ 
      rankdir: direction,
      nodesep: 150,  // Increased horizontal spacing between nodes
      ranksep: 200,  // Increased vertical spacing between ranks
      edgesep: 50,   // Minimum separation between edges
      marginx: 50,   // Margin around the graph
      marginy: 50
    });
  }

  // Set node dimensions - increase width for better readability
  nodes.forEach(node => {
    dagreGraph.setNode(node.id, { width: 220, height: 60 });
  });

  // Add edges
  edges.forEach(edge => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate layout
  dagre.layout(dagreGraph);

  // Apply layout positions to nodes
  const layoutedNodes = nodes.map(node => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - 110, // Center the node
        y: nodeWithPosition.y - 30
      }
    };
  });
  
  // Enhanced post-processing for root nodes and their dependency groups
  if (rootNodes.length > 0) {
    // Calculate total width needed for all root nodes
    const rootNodeWidth = 300; // Width of each root node + margin
    const totalWidth = rootNodes.length * rootNodeWidth;
    const startX = 100; // Left margin
    
    // Fixed Y position for all root nodes - place them at the bottom
    const rootY = 500; 
    
    // Sort root nodes by their dependency count for better layout
    const sortedRootNodes = [...rootNodes].sort((a, b) => {
      const depsA = dependencyGroups.get(a.id)?.size || 0;
      const depsB = dependencyGroups.get(b.id)?.size || 0;
      return depsB - depsA; // Nodes with more dependencies first
    });
    
    // Create a map to store the final positions of all nodes
    const finalPositions = new Map<string, { x: number, y: number }>();
    
    // Position each root node and adjust its dependency group
    sortedRootNodes.forEach((rootNode, index) => {
      // Position the root node
      const rootX = startX + (index * rootNodeWidth) + (rootNodeWidth / 2);
      
      // Store the root node position
      finalPositions.set(rootNode.id, { x: rootX, y: rootY });
      
      // Find the original position of the root node from dagre
      const originalRootPos = layoutedNodes.find(n => n.id === rootNode.id)?.position;
      if (!originalRootPos) return;
      
      // Calculate the offset to apply to all nodes in this group
      const xOffset = rootX - originalRootPos.x;
      
      // Get all dependencies of this root node
      const dependencies = dependencyGroups.get(rootNode.id) || new Set<string>();
      
      // Adjust the positions of all dependencies
      dependencies.forEach(depId => {
        const depNode = layoutedNodes.find(n => n.id === depId);
        if (!depNode) return;
        
        // Apply the X offset to align with the root node
        // Keep the Y position from dagre to maintain the hierarchy
        finalPositions.set(depId, {
          x: depNode.position.x + xOffset,
          y: depNode.position.y
        });
      });
    });
    
    // Apply the final positions to all nodes
    return {
      nodes: layoutedNodes.map(node => {
        const finalPos = finalPositions.get(node.id);
        if (finalPos) {
          return {
            ...node,
            position: finalPos,
            // Add visual grouping information
            data: {
              ...node.data,
              // Find which root node this belongs to
              rootNodeId: rootNodes.find(rootNode => 
                rootNode.id === node.id || 
                dependencyGroups.get(rootNode.id)?.has(node.id)
              )?.id
            }
          };
        }
        return node;
      }),
      // Enhance edges with group information
      edges: edges.map(edge => {
        // Find which root node group this edge belongs to
        const rootNodeId = rootNodes.find(rootNode => 
          rootNode.id === edge.source || 
          rootNode.id === edge.target ||
          dependencyGroups.get(rootNode.id)?.has(edge.source) ||
          dependencyGroups.get(rootNode.id)?.has(edge.target)
        )?.id;
        
        if (!rootNodeId) return edge;
        
        // Find the index of this root node
        const groupIndex = sortedRootNodes.findIndex(node => node.id === rootNodeId);
        
        return {
          ...edge,
          data: {
            ...edge.data,
            rootNodeId,
            groupIndex
          }
        };
      })
    };
  }

  return { nodes: layoutedNodes, edges };
};

/**
 * Apply force-directed layout to nodes and edges
 */
export const getForceLayoutedElements = (
  nodes: Node[], 
  edges: Edge[]
): { nodes: Node[], edges: Edge[] } => {
  if (nodes.length === 0) return { nodes, edges };

  // Simple force layout implementation
  const nodePositions: Record<string, { x: number, y: number }> = {};
  const repulsionForce = 300;
  const attractionForce = 0.1;
  
  // Initialize random positions
  nodes.forEach(node => {
    nodePositions[node.id] = {
      x: Math.random() * 800,
      y: Math.random() * 600
    };
  });
  
  // Run simulation steps
  for (let i = 0; i < 100; i++) {
    // Apply repulsion between all nodes
    for (let j = 0; j < nodes.length; j++) {
      for (let k = j + 1; k < nodes.length; k++) {
        const nodeA = nodes[j];
        const nodeB = nodes[k];
        const posA = nodePositions[nodeA.id];
        const posB = nodePositions[nodeB.id];
        
        const dx = posB.x - posA.x;
        const dy = posB.y - posA.y;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;
        const force = repulsionForce / (distance * distance);
        
        const forceX = (dx / distance) * force;
        const forceY = (dy / distance) * force;
        
        posA.x -= forceX;
        posA.y -= forceY;
        posB.x += forceX;
        posB.y += forceY;
      }
    }
    
    // Apply attraction along edges
    edges.forEach(edge => {
      const sourcePos = nodePositions[edge.source];
      const targetPos = nodePositions[edge.target];
      
      if (sourcePos && targetPos) {
        const dx = targetPos.x - sourcePos.x;
        const dy = targetPos.y - sourcePos.y;
        
        const forceX = dx * attractionForce;
        const forceY = dy * attractionForce;
        
        sourcePos.x += forceX;
        sourcePos.y += forceY;
        targetPos.x -= forceX;
        targetPos.y -= forceY;
      }
    });
  }
  
  // Apply calculated positions to nodes
  const layoutedNodes = nodes.map(node => ({
    ...node,
    position: nodePositions[node.id]
  }));
  
  return { nodes: layoutedNodes, edges };
};

/**
 * Apply radial layout to nodes and edges
 */
export const getRadialLayoutedElements = (
  nodes: Node[], 
  edges: Edge[]
): { nodes: Node[], edges: Edge[] } => {
  if (nodes.length === 0) return { nodes, edges };

  // Find root nodes (nodes with no incoming edges)
  const hasIncomingEdges: Record<string, boolean> = {};
  edges.forEach(edge => {
    hasIncomingEdges[edge.target] = true;
  });
  
  const rootNodes = nodes.filter(node => !hasIncomingEdges[node.id]);
  
  // If no root nodes, use the first node
  if (rootNodes.length === 0 && nodes.length > 0) {
    rootNodes.push(nodes[0]);
  }
  
  // Group nodes by their distance from root
  const nodeDistances: Record<string, number> = {};
  rootNodes.forEach(root => {
    nodeDistances[root.id] = 0;
    
    // BFS to calculate distances
    const queue = [root.id];
    const visited = new Set([root.id]);
    
    while (queue.length > 0) {
      const current = queue.shift()!;
      const currentDistance = nodeDistances[current];
      
      // Find outgoing edges
      edges.forEach(edge => {
        if (edge.source === current && !visited.has(edge.target)) {
          visited.add(edge.target);
          queue.push(edge.target);
          nodeDistances[edge.target] = currentDistance + 1;
        }
      });
    }
  });
  
  // Calculate max distance
  const maxDistance = Math.max(...Object.values(nodeDistances), 0);
  
  // Position nodes in concentric circles
  const layoutedNodes = nodes.map(node => {
    const distance = nodeDistances[node.id] || 0;
    const radius = (distance + 1) * 150;
    const nodesAtDistance = nodes.filter(n => (nodeDistances[n.id] || 0) === distance).length;
    const angleStep = (2 * Math.PI) / Math.max(nodesAtDistance, 1);
    const nodesAtSameDistance = nodes.filter(n => (nodeDistances[n.id] || 0) === distance);
    const indexAtDistance = nodesAtSameDistance.findIndex(n => n.id === node.id);
    const angle = indexAtDistance * angleStep;
    
    return {
      ...node,
      position: {
        x: 400 + radius * Math.cos(angle),
        y: 300 + radius * Math.sin(angle)
      }
    };
  });
  
  return { nodes: layoutedNodes, edges };
};
